import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { Spinner } from "@/components/ui/Spinner";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth";
import apiClient from "@/api/client";
import React, { useState } from "react";
import { clsx } from "clsx";
import { downloadCsv, toCsv } from "@/utils/download";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import {
  Clock,
  Activity,
  Download,
  RefreshCw,
  Filter,
  LineChart as LineChartIcon,
  Plus,
  X,
  Check,
  Search,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DeclaredMarks,
  declaredToTs,
  hhmmToHours,
  type DeclaredMark,
  type DeclaredPeriod,
} from "@/features/dev_issues/components/DeclaredMarks";

interface UserRequestRecord {
  path: string;
  method: string;
  status_code: number | null;
  ts: string;
}

interface UserActivity {
  user_id: string;
  user_name: string;
  profile_picture_url: string | null;
  total_requests: number;
  first_request: string | null;
  last_request: string | null;
  work_minutes: number;
  top_paths: { path: string; count: number }[];
  recent_requests: UserRequestRecord[];
  error_requests: UserRequestRecord[];
}

interface ActivityData {
  period_label: string;
  users: UserActivity[];
}

const PERIODS = [
  { key: "today", label: "Dzisiaj" },
  { key: "24h", label: "24h" },
  { key: "7d", label: "7 dni" },
  { key: "30d", label: "30 dni" },
];

function formatWorkTime(minutes: number): string {
  if (minutes < 1) return "< 1 min";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h === 0) return `${m} min`;
  return `${h} godz. ${m} min`;
}

function formatTimeParts(iso: string | null): { date: string; time: string } | null {
  if (!iso) return null;
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return { date: formatDate(d), time: `${hh}:${mm}:${ss}.${ms}` };
}

function isActiveNow(lastRequest: string | null): boolean {
  if (!lastRequest) return false;
  return Date.now() - new Date(lastRequest).getTime() < 5 * 60 * 1000;
}

function statusColor(code: number | null): string {
  if (!code) return "text-gray-400";
  if (code >= 500) return "text-red-600 dark:text-red-400";
  if (code >= 400) return "text-amber-600 dark:text-amber-400";
  return "text-green-600 dark:text-green-400";
}

const METHOD_COLORS: Record<string, string> = {
  GET: "text-[var(--color-primary)]",
  POST: "text-green-500",
  PATCH: "text-amber-500",
  PUT: "text-amber-500",
  DELETE: "text-red-500",
};

interface RequestsPage {
  requests: UserRequestRecord[];
  has_more: boolean;
  next_cursor: string | null;
}

function RequestList({
  rows,
  footer,
}: {
  rows: UserRequestRecord[];
  footer?: React.ReactNode;
}) {
  return rows.length === 0 ? (
    <p className="text-xs text-gray-400">Brak danych</p>
  ) : (
    <div className="flex-1 overflow-y-auto space-y-0.5">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="text-gray-400 w-44 flex-shrink-0 tabular-nums inline-flex items-center gap-0.5">
            {(() => {
              const p = formatTimeParts(r.ts);
              if (!p) return "—";
              return (
                <>
                  <span className="opacity-50">{p.date}</span>
                  <span className="opacity-30 mx-0.5">·</span>
                  <span>{p.time}</span>
                </>
              );
            })()}
          </span>
          <span
            className={clsx(
              "font-mono font-semibold w-12 flex-shrink-0",
              METHOD_COLORS[r.method] ?? "text-gray-500",
            )}
          >
            {r.method}
          </span>
          <span
            className={clsx(
              "w-8 flex-shrink-0 tabular-nums font-medium",
              statusColor(r.status_code),
            )}
          >
            {r.status_code ?? "—"}
          </span>
          <span
            className="font-mono text-gray-600 dark:text-gray-400 truncate"
            title={r.path}
          >
            {r.path}
          </span>
        </div>
      ))}
      {footer && <div className="pt-2 flex justify-center">{footer}</div>}
    </div>
  );
}

function RecentRequestsPanel({
  userId,
  period,
  excludePolling,
  errorRequests,
}: {
  userId: string;
  period: string;
  excludePolling: boolean;
  errorRequests: UserRequestRecord[];
}) {
  const [tab, setTab] = useState<"all" | "errors">("all");

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["dev-user-requests", userId, period, excludePolling],
      queryFn: ({ pageParam }) =>
        apiClient
          .get<{ data: RequestsPage }>(`/dev-issues/user-requests/`, {
            params: {
              user_id: userId,
              period,
              ...(excludePolling ? { exclude_polling: 1 } : {}),
              ...(pageParam ? { before: pageParam } : {}),
            },
          })
          .then((r) => r.data.data),
      initialPageParam: undefined as string | undefined,
      getNextPageParam: (last) => last.next_cursor ?? undefined,
    });

  const allRequests = data?.pages.flatMap((p) => p.requests) ?? [];

  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 p-4 overflow-hidden max-h-72 md:max-h-none">
      {/* Tab header */}
      <div className="flex items-center gap-2 mb-3 flex-shrink-0">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Requesty
        </p>
        <div className="flex gap-1 ml-auto">
          <button
            onClick={() => setTab("all")}
            className={clsx(
              "px-2 py-0.5 text-xs rounded font-medium transition-colors",
              tab === "all"
                ? "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
            )}
          >
            Wszystkie{" "}
            {allRequests.length > 0 && (
              <span className="text-gray-400 font-normal">
                ({allRequests.length})
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("errors")}
            className={clsx(
              "px-2 py-0.5 text-xs rounded font-medium transition-colors flex items-center gap-1",
              tab === "errors"
                ? "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
            )}
          >
            Błędy
            {errorRequests.length > 0 && (
              <span
                className={clsx(
                  "px-1 py-0 rounded text-xs font-semibold",
                  tab === "errors"
                    ? "bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300"
                    : "bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400",
                )}
              >
                {errorRequests.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {tab === "errors" ? (
        <RequestList rows={errorRequests} />
      ) : (
        <RequestList
          rows={allRequests}
          footer={
            hasNextPage ? (
              <button
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] disabled:opacity-50 py-1 px-3"
              >
                {isFetchingNextPage ? "Ładowanie…" : "Załaduj starsze"}
              </button>
            ) : allRequests.length >= 100 ? (
              <span className="text-xs text-gray-400 py-1">Koniec zakresu</span>
            ) : undefined
          }
        />
      )}
    </div>
  );
}

interface TimeseriesData {
  period_label: string;
  bucket_unit: "quarter" | "hour" | "day";
  /** Bucket width in minutes; null for daily buckets. */
  bucket_minutes: number | null;
  buckets: string[];
  series: { user_id: string; user_name: string; counts: number[]; total: number }[];
  /** Declared work blocks per user id; only sent for hourly buckets. */
  declared?: Record<string, DeclaredPeriod[]>;
}

// Distinct, colour-blind-friendly line colours; index wraps if more series.
const SERIES_COLORS = [
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#ec4899",
  "#14b8a6",
  "#6366f1",
  "#f97316",
  "#84cc16",
];

function OverlayPicker({
  users,
  primaryId,
  overlayIds,
  onChange,
}: {
  users: UserActivity[];
  primaryId: string;
  overlayIds: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const candidates = users
    .filter((u) => u.user_id !== primaryId)
    .filter((u) =>
      u.user_name.toLowerCase().includes(filter.trim().toLowerCase()),
    );

  const toggle = (id: string) => {
    onChange(
      overlayIds.includes(id)
        ? overlayIds.filter((x) => x !== id)
        : [...overlayIds, id],
    );
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
      >
        <Plus size={13} />
        Nałóż użytkownika
        {overlayIds.length > 0 && (
          <span className="ml-0.5 px-1.5 py-0 rounded-full text-xs font-semibold bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
            {overlayIds.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
          <div className="flex items-center gap-1.5 px-2.5 py-2 border-b border-gray-100 dark:border-gray-700">
            <Search size={13} className="text-gray-400 flex-shrink-0" />
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Szukaj…"
              className="w-full bg-transparent text-xs text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {candidates.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400">Brak użytkowników</li>
            ) : (
              candidates.map((u) => {
                const checked = overlayIds.includes(u.user_id);
                return (
                  <li key={u.user_id}>
                    <button
                      onClick={() => toggle(u.user_id)}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <span
                        className={clsx(
                          "w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0",
                          checked
                            ? "bg-[var(--color-primary)] border-[var(--color-primary)]"
                            : "border-gray-300 dark:border-gray-600",
                        )}
                      >
                        {checked && <Check size={10} className="text-white" strokeWidth={3} />}
                      </span>
                      <span className="truncate text-gray-700 dark:text-gray-200">
                        {u.user_name}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

function ChartTooltip({ active, payload, label, unit, bucketMinutes }: any) {
  if (!active || !payload?.length) return null;
  const d = new Date(label);
  const hhmm = (x: Date) =>
    x.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  // Sub-hour buckets are ranges, not instants — label them as such.
  const title =
    unit === "day"
      ? d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" })
      : bucketMinutes
        ? `${formatDate(d)} ${hhmm(d)}–${hhmm(new Date(d.getTime() + bucketMinutes * 60_000))}`
        : formatDateTime(d);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">{title}</p>
      {payload
        .slice()
        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
        .map((p: any) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-gray-600 dark:text-gray-300 truncate flex-1">
              {p.name}
            </span>
            <span className="tabular-nums font-medium text-gray-800 dark:text-gray-100">
              {p.value ?? 0}
            </span>
          </div>
        ))}
    </div>
  );
}

function ActivityChart({
  users,
  primary,
  overlayIds,
  setOverlayIds,
  period,
  excludePolling,
}: {
  users: UserActivity[];
  primary: UserActivity;
  overlayIds: string[];
  setOverlayIds: (ids: string[]) => void;
  period: string;
  excludePolling: boolean;
}) {
  // Primary user is always charted; overlays are extra (never duplicate primary).
  const chartedIds = [
    primary.user_id,
    ...overlayIds.filter((id) => id !== primary.user_id),
  ];

  const { data, isLoading } = useQuery({
    queryKey: ["dev-user-activity-ts", chartedIds, period, excludePolling],
    queryFn: () =>
      apiClient
        .get<{ data: TimeseriesData }>(`/dev-issues/user-activity/timeseries/`, {
          params: {
            user_ids: chartedIds.join(","),
            period,
            ...(excludePolling ? { exclude_polling: 1 } : {}),
          },
        })
        .then((r) => r.data.data),
    refetchInterval: 60_000,
  });

  const unit = data?.bucket_unit ?? "quarter";
  const bucketMinutes = data?.bucket_minutes ?? null;
  // Daily buckets report no width; one day is the implied step.
  const halfBucketMs = ((bucketMinutes ?? 24 * 60) * 60_000) / 2;
  // x is epoch ms so declared start/stop marks can land between buckets.
  const chartData =
    data?.buckets.map((ts, i) => {
      const row: Record<string, number | string> = { ts, t: new Date(ts).getTime() };
      data.series.forEach((s) => {
        row[s.user_id] = s.counts[i] ?? 0;
      });
      return row;
    }) ?? [];

  const colorFor = (id: string) =>
    SERIES_COLORS[chartedIds.indexOf(id) % SERIES_COLORS.length];

  // Declared work blocks (karta godzin) → one lane per charted user.
  const declaredMarks: DeclaredMark[] = [];
  if (unit !== "day" && chartData.length > 0) {
    (data?.series ?? []).forEach((s, laneIdx) => {
      (data?.declared?.[s.user_id] ?? []).forEach((p, i) => {
        const x1 = declaredToTs(p.date, p.start);
        const x2 = declaredToTs(p.date, p.end);
        if (x1 == null || x2 == null) return;
        declaredMarks.push({
          key: `${s.user_id}-${p.date}-${i}`,
          color: colorFor(s.user_id),
          x1,
          x2,
          lane: laneIdx,
          title: `${s.user_name} · ${formatDate(p.date)} · ${p.label}`,
        });
      });
    });
  }

  const fmtTick = (v: string | number) => {
    const d = new Date(v);
    if (unit === "day")
      return d.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit" });
    return d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Aktywność requestowa
        </p>
        <div className="ml-auto">
          <OverlayPicker
            users={users}
            primaryId={primary.user_id}
            overlayIds={overlayIds.filter((id) => id !== primary.user_id)}
            onChange={setOverlayIds}
          />
        </div>
      </div>

      {overlayIds.filter((id) => id !== primary.user_id).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {chartedIds.map((id) => {
            const u = users.find((x) => x.user_id === id);
            const isPrimary = id === primary.user_id;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full text-xs border border-gray-200 dark:border-gray-700"
              >
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: colorFor(id) }}
                />
                <span className="text-gray-600 dark:text-gray-300">
                  {u?.user_name ?? id}
                </span>
                {!isPrimary && (
                  <button
                    onClick={() =>
                      setOverlayIds(overlayIds.filter((x) => x !== id))
                    }
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full"
                    title="Usuń z wykresu"
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="h-56 flex items-center justify-center">
          <Spinner />
        </div>
      ) : chartData.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-xs text-gray-400">
          Brak danych w wybranym okresie
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={224}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 8, left: 0, bottom: 0 }}
            barGap={0}
            maxBarSize={40}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
            <XAxis
              dataKey="t"
              type="number"
              scale="time"
              // Half a bucket of padding on both ends so the first and last
              // bars are drawn whole instead of clipped by the plot edge.
              domain={[
                (min: number) => min - halfBucketMs,
                (max: number) => max + halfBucketMs,
              ]}
              tickFormatter={fmtTick}
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip
              content={<ChartTooltip unit={unit} bucketMinutes={bucketMinutes} />}
              cursor={{ fill: "#9ca3af", fillOpacity: 0.12 }}
            />
            {chartedIds.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                formatter={(v) => <span style={{ color: "#9ca3af" }}>{v}</span>}
              />
            )}
            {(data?.series ?? []).map((s) => (
              <Bar
                key={s.user_id}
                dataKey={s.user_id}
                name={s.user_name}
                fill={colorFor(s.user_id)}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
              />
            ))}
            {declaredMarks.length > 0 && (
              <Customized component={<DeclaredMarks marks={declaredMarks} />} />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
      {unit !== "day" && (
        <p className="text-[11px] text-gray-400 mt-2">
          Słupek = liczba requestów w oknie {bucketMinutes ?? 15} minut. Kropki u
          dołu = deklarowane okresy pracy z karty godzin (pełna kropka — start,
          pusta — koniec). Pasek w kolorze serii użytkownika.
        </p>
      )}
    </div>
  );
}

// ISO (YYYY-MM-DD) strings for the last N calendar days, oldest first.
function lastNDates(n: number): string[] {
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - i);
    out.push(
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`,
    );
  }
  return out;
}

function fmtDayLabel(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return `${d.toLocaleDateString("pl-PL", { weekday: "short" })} ${formatDate(d)}`;
}

interface DayComparisonData {
  user_id: string;
  user_name: string;
  /** Bucket width in minutes (15). */
  slot_minutes: number;
  /** Bucket starts as decimal hours: 0, 0.25, 0.5, … */
  slots: number[];
  days: {
    date: string;
    is_remote: boolean;
    counts: number[];
    total: number;
    declared?: DeclaredPeriod[];
  }[];
}

// Decimal hours (9.25) → "09:15".
function hoursToHhmm(h: number): string {
  const total = Math.round(h * 60);
  return `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function DayComparisonTooltip({ active, payload, label, slotMinutes }: any) {
  if (!active || !payload?.length) return null;
  const start = Number(label);
  const end = start + (slotMinutes ?? 15) / 60;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1">
        {hoursToHhmm(start)}–{hoursToHhmm(end)}
      </p>
      {payload
        .slice()
        .sort((a: any, b: any) => (b.value ?? 0) - (a.value ?? 0))
        .map((p: any) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: p.color }}
            />
            <span className="text-gray-600 dark:text-gray-300 truncate flex-1">
              {fmtDayLabel(String(p.name))}
            </span>
            <span className="tabular-nums font-medium text-gray-800 dark:text-gray-100">
              {p.value ?? 0}
            </span>
          </div>
        ))}
    </div>
  );
}

function DayComparisonChart({
  primary,
  dates,
  setDates,
  excludePolling,
}: {
  primary: UserActivity;
  dates: string[];
  setDates: (d: string[]) => void;
  excludePolling: boolean;
}) {
  const sortedDates = [...dates].sort();

  const { data, isLoading } = useQuery({
    queryKey: [
      "dev-user-day-comparison",
      primary.user_id,
      sortedDates,
      excludePolling,
    ],
    queryFn: () =>
      apiClient
        .get<{ data: DayComparisonData }>(
          `/dev-issues/user-activity/day-comparison/`,
          {
            params: {
              user_id: primary.user_id,
              ...(sortedDates.length ? { dates: sortedDates.join(",") } : {}),
              ...(excludePolling ? { exclude_polling: 1 } : {}),
            },
          },
        )
        .then((r) => r.data.data),
    enabled: sortedDates.length > 0,
  });

  const days = data?.days ?? [];
  const slotMinutes = data?.slot_minutes ?? 15;
  // One point per 15-minute slot; x stays in hours so the axis is unchanged.
  const slots = data?.slots ?? [];
  const chartData = slots.map((hour, i) => {
    const row: Record<string, number | string> = { hour };
    days.forEach((d) => {
      row[d.date] = d.counts[i] ?? 0;
    });
    return row;
  });

  const colorFor = (iso: string) =>
    SERIES_COLORS[sortedDates.indexOf(iso) % SERIES_COLORS.length];

  // Declared start/stop from the timesheet draft — one lane per day that has any.
  const declaredMarks: DeclaredMark[] = [];
  days
    .filter((d) => (d.declared ?? []).length > 0)
    .forEach((d, laneIdx) => {
      (d.declared ?? []).forEach((p, i) => {
        const x1 = hhmmToHours(p.start);
        const x2 = hhmmToHours(p.end);
        if (x1 == null || x2 == null) return;
        declaredMarks.push({
          key: `${d.date}-${i}`,
          color: colorFor(d.date),
          x1,
          x2,
          lane: laneIdx,
          title: `${fmtDayLabel(d.date)} · ${p.label}`,
        });
      });
    });

  const addDate = (iso: string) => {
    if (!iso || dates.includes(iso)) return;
    if (dates.length >= 14) return;
    setDates([...dates, iso]);
  };
  const removeDate = (iso: string) => setDates(dates.filter((d) => d !== iso));

  const todayIso = lastNDates(1)[0];

  return (
    <div className="flex flex-col bg-white dark:bg-gray-800 p-4">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          Dzień po dniu — {primary.user_name}
        </p>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => setDates(lastNDates(7))}
            className="px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Ostatnie 7 dni
          </button>
          <label className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
            <Plus size={13} />
            Dodaj dzień
            <input
              type="date"
              max={todayIso}
              value=""
              onChange={(e) => addDate(e.target.value)}
              className="sr-only"
            />
          </label>
        </div>
      </div>

      {/* Selected-day chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {sortedDates.length === 0 ? (
          <span className="text-xs text-gray-400">
            Wybierz co najmniej jeden dzień
          </span>
        ) : (
          sortedDates.map((iso) => {
            const dayInfo = days.find((d) => d.date === iso);
            return (
              <span
                key={iso}
                className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full text-xs border border-gray-200 dark:border-gray-700"
                title={dayInfo?.is_remote ? "Praca zdalna" : undefined}
              >
                <span
                  className="w-4 h-0 border-t-2"
                  style={{
                    borderColor: colorFor(iso),
                    borderTopStyle: dayInfo?.is_remote ? "dashed" : "solid",
                  }}
                />
                <span className="text-gray-600 dark:text-gray-300">
                  {fmtDayLabel(iso)}
                </span>
                {dayInfo?.is_remote && (
                  <span className="text-[10px] text-[var(--color-primary)] font-medium">
                    zdalny
                  </span>
                )}
                <button
                  onClick={() => removeDate(iso)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full"
                  title="Usuń dzień"
                >
                  <X size={12} />
                </button>
              </span>
            );
          })
        )}
      </div>

      {isLoading ? (
        <div className="h-56 flex items-center justify-center">
          <Spinner />
        </div>
      ) : sortedDates.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-xs text-gray-400">
          Brak wybranych dni
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={224}>
          <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.2} />
            <XAxis
              dataKey="hour"
              type="number"
              domain={[0, 24]}
              ticks={[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]}
              tickFormatter={(h) => `${String(h).padStart(2, "0")}`}
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip content={<DayComparisonTooltip slotMinutes={slotMinutes} />} />
            <Legend
              wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
              formatter={(v) => (
                <span style={{ color: "#9ca3af" }}>{fmtDayLabel(String(v))}</span>
              )}
            />
            {days.map((d) => (
              <Line
                key={d.date}
                type="monotone"
                dataKey={d.date}
                name={d.date}
                stroke={colorFor(d.date)}
                strokeWidth={1.75}
                strokeDasharray={d.is_remote ? "5 3" : undefined}
                dot={false}
                connectNulls
              />
            ))}
            {declaredMarks.length > 0 && (
              <Customized component={<DeclaredMarks marks={declaredMarks} />} />
            )}
          </LineChart>
        </ResponsiveContainer>
      )}
      <p className="text-[11px] text-gray-400 mt-2">
        Oś X = godzina doby, jeden punkt = {slotMinutes} minut. Linia przerywana =
        dzień pracy zdalnej. Kropki u dołu
        = deklarowane okresy pracy z karty godzin (pełna kropka — start, pusta —
        koniec), po jednym pasku na dzień.
      </p>
    </div>
  );
}

function exportCsv(users: UserActivity[], periodLabel: string) {
  const rows = [
    [
      "Użytkownik",
      "Requesty",
      "Pierwsza aktywność",
      "Ostatnia aktywność",
      "Czas pracy (min)",
    ],
    ...users.map((u) => [
      u.user_name,
      u.total_requests,
      u.first_request ?? "",
      u.last_request ?? "",
      u.work_minutes,
    ]),
  ];
  downloadCsv(toCsv(rows), `aktywnosc_${periodLabel.replace(/\s/g, "_")}.csv`);
}

export function DevActivityPage() {
  const user = useAuthStore((s) => s.user);
  const isDevOrDirector = !!(user?.is_developer || user?.role === "director");
  const [period, setPeriod] = useState("24h");
  const [excludePolling, setExcludePolling] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showChart, setShowChart] = useState(false);
  const [chartMode, setChartMode] = useState<"users" | "days">("users");
  const [overlayIds, setOverlayIds] = useState<string[]>([]);
  const [compareDates, setCompareDates] = useState<string[]>(() => lastNDates(7));

  const { data, isLoading, dataUpdatedAt, refetch } = useQuery({
    queryKey: ["dev-user-activity", period, excludePolling],
    queryFn: () =>
      apiClient
        .get<{
          data: ActivityData;
        }>(
          `/dev-issues/user-activity/?period=${period}${excludePolling ? "&exclude_polling=1" : ""}`,
        )
        .then((r) => r.data.data),
    refetchInterval: 60_000,
    enabled: isDevOrDirector,
  });

  if (!isDevOrDirector) return <Navigate to="/" replace />;

  const users = data?.users ?? [];
  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString("pl-PL")
    : "—";
  const activeNow = users.filter((u) => isActiveNow(u.last_request)).length;

  const sortedUsers = [...users].sort((a, b) => {
    const aActive = isActiveNow(a.last_request);
    const bActive = isActiveNow(b.last_request);
    if (aActive !== bActive) return aActive ? -1 : 1;
    return a.user_name.localeCompare(b.user_name, "pl");
  });
  const selected = selectedUserId
    ? (users.find((u) => u.user_id === selectedUserId) ??
      sortedUsers[0] ??
      null)
    : (sortedUsers[0] ?? null);

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Aktywność użytkowników
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-1" />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 flex flex-wrap items-center gap-x-1">
            <span>{data?.period_label ?? "…"}</span>
            <span className="hidden sm:inline">· odświeżanie co 60s ·</span>
            <span>ostatnio: {lastUpdated}</span>
            {activeNow > 0 && (
              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                {activeNow} {activeNow === 1 ? "aktywny" : "aktywnych"} teraz
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="flex items-center gap-2">
            {users.length > 0 && (
              <button
                onClick={() => exportCsv(users, data?.period_label ?? period)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Download size={14} />
                CSV
              </button>
            )}
            <button
              onClick={() => refetch()}
              title="Odśwież dane"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <RefreshCw size={14} />
              Odśwież
            </button>
            <button
              onClick={() => setExcludePolling((v) => !v)}
              title="Ukryj requesty odpytywane automatycznie przez aplikację (powiadomienia, badge, realtime, heartbeat) — pokazuj tylko realną aktywność użytkownika"
              className={clsx(
                "flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border rounded-lg transition-colors",
                excludePolling
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/5"
                  : "text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700",
              )}
            >
              <Filter size={14} />
              Bez cyklicznych
            </button>
          </div>
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-full md:w-auto">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                className={clsx(
                  "flex-1 md:flex-none px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                  period === p.key
                    ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center py-8">
          <Spinner />
        </div>
      ) : users.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-sm text-gray-400 gap-2">
          <Activity size={32} className="opacity-30" />
          Brak aktywności w wybranym okresie
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-4">
          {/* Mobile — horizontal avatar strip */}
          <div className="md:hidden flex-shrink-0">
            <p className="px-1 pb-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Użytkownicy ({users.length})
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {sortedUsers.map((u) => {
                const active = isActiveNow(u.last_request);
                const isSelected = u.user_id === selected?.user_id;
                return (
                  <button
                    key={u.user_id}
                    onClick={() => setSelectedUserId(u.user_id)}
                    className={clsx(
                      "flex flex-col items-center gap-1 px-2 pt-2 pb-1.5 rounded-lg flex-shrink-0 transition-colors border-b-2",
                      isSelected
                        ? "border-[var(--color-primary)] bg-gray-50 dark:bg-gray-700/40"
                        : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/20",
                    )}
                  >
                    <span className="relative">
                      <UserAvatar
                        userId={u.user_id}
                        fullName={u.user_name}
                        profilePictureUrl={u.profile_picture_url}
                        size="xs"
                      />
                      <span
                        className={clsx(
                          "absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-gray-800",
                          active ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600",
                        )}
                      />
                    </span>
                    <span className="text-[10px] w-14 text-center leading-tight line-clamp-2 text-gray-700 dark:text-gray-300">
                      {u.user_name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop — vertical user list */}
          <div className="hidden md:flex w-52 flex-shrink-0 flex-col overflow-hidden">
            <p className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              Użytkownicy ({users.length})
            </p>
            <ul className="flex-1 overflow-y-auto">
              {sortedUsers.map((u) => {
                const active = isActiveNow(u.last_request);
                const isSelected = u.user_id === selected?.user_id;
                return (
                  <li key={u.user_id}>
                    <button
                      onClick={() => setSelectedUserId(u.user_id)}
                      className={clsx(
                        "w-full text-left pl-0 pr-3 py-2.5 flex items-center gap-2 transition-colors border-l-2",
                        isSelected
                          ? "border-[var(--color-primary)] bg-gray-50 dark:bg-gray-700/40 text-gray-900 dark:text-gray-100 font-semibold"
                          : "border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-700 dark:text-gray-300",
                      )}
                    >
                      <span className="pl-2 flex-shrink-0 relative">
                        <UserAvatar
                          userId={u.user_id}
                          fullName={u.user_name}
                          profilePictureUrl={u.profile_picture_url}
                          size="xs"
                        />
                        <span
                          className={clsx(
                            "absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-gray-800",
                            active ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600",
                          )}
                        />
                      </span>
                      <span className="text-sm truncate flex-1" title={u.user_name}>
                        {u.user_name}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right — detail */}
          {selected && (
            <div className="flex-1 min-w-0 flex flex-col gap-3">
              {/* Compact stats strip */}
              <div className="flex-shrink-0 bg-white dark:bg-gray-800 px-4 py-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="relative flex-shrink-0">
                    <UserAvatar
                      userId={selected.user_id}
                      fullName={selected.user_name}
                      profilePictureUrl={selected.profile_picture_url}
                      size="sm"
                    />
                    <span
                      className={clsx(
                        "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-gray-800",
                        isActiveNow(selected.last_request)
                          ? "bg-green-500"
                          : "bg-gray-300 dark:bg-gray-600",
                      )}
                    />
                  </span>
                  <span className="font-bold text-gray-900 dark:text-white">
                    {selected.user_name}
                  </span>
                  {isActiveNow(selected.last_request) && (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                      aktywny teraz
                    </span>
                  )}
                  <button
                    onClick={() => setShowChart((v) => !v)}
                    className={clsx(
                      "ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-lg transition-colors",
                      showChart
                        ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary)]/5"
                        : "text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700",
                    )}
                  >
                    <LineChartIcon size={13} />
                    Wykres
                  </button>
                </div>
                <div className="flex items-center gap-4 flex-wrap mt-2 md:mt-0 md:hidden">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Requesty</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                      {selected.total_requests.toLocaleString("pl-PL")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={12} className="text-gray-400" />
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatWorkTime(selected.work_minutes)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Od</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                      {formatDateTime(selected.first_request)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Do</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                      {formatDateTime(selected.last_request)}
                    </span>
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-5 flex-wrap mt-0">
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-xs text-gray-400">Requesty</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">
                      {selected.total_requests.toLocaleString("pl-PL")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock size={12} className="text-gray-400" />
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {formatWorkTime(selected.work_minutes)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Od</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                      {formatDateTime(selected.first_request)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Do</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
                      {formatDateTime(selected.last_request)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Activity chart (toggleable) */}
              {showChart && (
                <div className="flex-shrink-0 bg-white dark:bg-gray-800">
                  <div className="flex items-center gap-1 px-4 pt-3">
                    <div className="flex gap-1 bg-gray-100 dark:bg-gray-700/50 rounded-lg p-0.5">
                      <button
                        onClick={() => setChartMode("users")}
                        className={clsx(
                          "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
                          chartMode === "users"
                            ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200",
                        )}
                      >
                        Użytkownicy
                      </button>
                      <button
                        onClick={() => setChartMode("days")}
                        className={clsx(
                          "px-2.5 py-1 text-xs font-medium rounded-md transition-colors",
                          chartMode === "days"
                            ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200",
                        )}
                      >
                        Dzień po dniu
                      </button>
                    </div>
                  </div>
                  {chartMode === "users" ? (
                    <ActivityChart
                      users={users}
                      primary={selected}
                      overlayIds={overlayIds}
                      setOverlayIds={setOverlayIds}
                      period={period}
                      excludePolling={excludePolling}
                    />
                  ) : (
                    <DayComparisonChart
                      primary={selected}
                      dates={compareDates}
                      setDates={setCompareDates}
                      excludePolling={excludePolling}
                    />
                  )}
                </div>
              )}

              {/* Two panels — fill remaining height */}
              <div className="flex-1 min-h-0 grid gap-3 grid-cols-1 md:grid-cols-[260px_1fr]">
                {/* Top endpoints */}
                <div className="flex flex-col bg-white dark:bg-gray-800 p-4 overflow-hidden">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex-shrink-0">
                    Top endpointy
                  </p>
                  {selected.top_paths.length === 0 ? (
                    <p className="text-xs text-gray-400">Brak danych</p>
                  ) : (
                    <div className="max-h-48 md:max-h-none flex-1 overflow-y-auto space-y-1.5">
                      {selected.top_paths.map((p, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span
                            className="text-xs font-mono text-gray-600 dark:text-gray-300 truncate flex-1"
                            title={p.path}
                          >
                            {p.path}
                          </span>
                          <span className="text-xs text-gray-400 tabular-nums flex-shrink-0">
                            ×{p.count}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <RecentRequestsPanel
                  userId={selected.user_id}
                  period={period}
                  excludePolling={excludePolling}
                  errorRequests={selected.error_requests ?? []}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* <p className="text-xs text-gray-400 dark:text-gray-600 flex-shrink-0">
        Czas pracy: suma sesji (przerwa &gt;2 min = koniec sesji, doliczane 2 min po ostatnim requeście).
      </p> */}
    </div>
  );
}
